import React, { useState, useRef, useEffect } from "react";
import { CallPanel } from "../calls/CallPanel";

import Button from "@mui/material/Button";
import EllipsisButtonWithMenu from "../EllipsisButtonWithMenu.tsx";
import ResponseButtonWithMenu from "../ResponseButtonWithMenu.tsx";
import ChatContent from "../chat/ChatContent.tsx";
import ChatHeader from "../chat/ChatHeader.tsx";
import ChatFooter from "../chat/ChatFooter.tsx";
import SchedulingInterface from "../scheduling/SchedulingInterface.tsx";
import Remainder from "../Remainder.tsx"; 
// import { PropertyList } from "../properties/PropertyList.tsx";
import apiService from "../../services/api";
import { ChatItem } from "../../types/chat.ts";
import { formatPhoneNumber } from "../../utils/utils";

const LeadContent = ({
  activeId,
  leads,
  handleChangeLeadData,
  handleDeleteLead,
  onBack,
  isMobileView,
}) => {
  const [profileIsOpen, setProfileIsOpen] = useState(false);
  // Track latest lead snapshot in a ref to avoid adding lead fields to deps
  const latestLeadRef = useRef<any>(null);
  useEffect(() => {
    latestLeadRef.current = leads?.find?.((l) => String(l.id) === String(activeId)) || null;
  }, [leads, activeId]);
  const [chatData, setChatData] = useState<ChatItem[]>([]);
  const [insightsState, setInsightsState] = useState<Record<string, any>>({});
  const handleClearChat = () => {
    setChatData([]);
  };
  const [remainderOpen, setRemainderOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const bellRef = useRef(null);
  const lastConversationCountByLeadRef = useRef<Record<string, number>>({});
  const [nextAction, setNextAction] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [activeCall, setActiveCall] = useState<{ callId: string; conferenceName?: string; status?: string } | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [addingProperty, setAddingProperty] = useState(false);
  const [newProperty, setNewProperty] = useState<any>({ location: '', area: '', rooms: '', bathrooms: '' });
  const [interestedProperties, setInterestedProperties] = useState<any[]>([]);
  // removed manual refresh state

  const toRelativeTime = (ts?: string | number) => {
    if (!ts) return '';
    const date = new Date(ts);
    const diff = Date.now() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Fetch interested properties when lead changes or on component mount
  useEffect(() => {
    const fetchInterestedProperties = async () => {
      if (!activeId) return;
      try {
        const result = await apiService.getInterestedProperties(activeId);
        setInterestedProperties(result.interestedProperties || []);
      } catch (error) {
        console.error('Failed to fetch interested properties:', error);
        // Fallback to reading from lead data
        const lead = leads.find((lead) => String(lead.id) === String(activeId));
        const interestedProperties: any[] = (lead as any)?.rawLead?.interestedProperties
          || (lead as any)?.rawJson?.interestedProperties
          || [];
        setInterestedProperties(interestedProperties);
      }
    };

    fetchInterestedProperties();
  }, [activeId, leads]);

  // Also fetch interested properties when the component mounts to ensure data is fresh
  useEffect(() => {
    const refreshInterestedProperties = async () => {
      if (!activeId) return;
      try {
        const result = await apiService.getInterestedProperties(activeId);
        setInterestedProperties(result.interestedProperties || []);
      } catch (error) {
        console.warn('Could not refresh interested properties on mount:', error);
      }
    };

    // Small delay to ensure backend is fully started
    const timer = setTimeout(refreshInterestedProperties, 1000);
    return () => clearTimeout(timer);
  }, [activeId]); // Run once on mount

  useEffect(() => {
    const leadId = String(activeId ?? "");
    if (!leadId) return;

    let cancelled = false;
    let pollInterval: NodeJS.Timeout | null = null;
    let eventSource: EventSource | null = null;

    const mapConversationsToChatItems = (items: any[]): ChatItem[] => {
      return (items || []).map((it, idx) => {
        const id = Number(it?.id ?? idx + 1);
        const createdAt = it?.createdAt ?? it?.timestamp ?? undefined;
        const content = it?.content ?? it?.message ?? '';
        const rawRole = String(it?.role ?? it?.author ?? it?.authorType ?? '').toLowerCase();
        const rawDir = String(it?.direction ?? it?.dir ?? '').toLowerCase();

        const isMyMessage =
          rawDir === 'outbound' || rawDir === 'out'
            ? true
            : rawDir === 'inbound' || rawDir === 'in'
              ? false
              : ['agent', 'realtor', 'assistant', 'system'].includes(rawRole)
                ? true
                : ['client', 'lead', 'buyer', 'prospect', 'user'].includes(rawRole)
                  ? false
                  : false;

        return {
          id,
          isConversation: false,
          isMyMessage,
          minAgo: toRelativeTime(createdAt),
          message: String(content),
          direction: rawDir || undefined,
        } as ChatItem;
      });
    };

    const fetchChatData = async (isManualRefresh = false, source = 'unknown') => {
      try {
        // manual refresh removed; keep logic simple
        
        console.log(`🔄 [REALTIME] Fetching chat data (source: ${source}) for lead: ${leadId}`);
        
        const [conversations, nba] = await Promise.all([
          apiService.getLeadConversations(leadId),
          apiService.getNextBestAction(leadId).catch(() => ""),
        ]);
        if (cancelled) return;
        
        const newChatData = mapConversationsToChatItems(conversations);
        const prevCount = lastConversationCountByLeadRef.current[leadId] ?? 0;
        const hasNewMessages = newChatData.length > prevCount;
        const newMessages = hasNewMessages ? newChatData.slice(prevCount) : [];
        const hasNewClientMessage = newMessages.some(msg => {
          const rawDir = String((msg as any)?.direction || '').toLowerCase();
          return rawDir === 'inbound' || (rawDir !== 'outbound' && !msg.isMyMessage);
        });
        lastConversationCountByLeadRef.current[leadId] = newChatData.length;

        // Only update if data has changed to avoid unnecessary re-renders
        setChatData(prevData => {
          const hasChanged = JSON.stringify(prevData) !== JSON.stringify(newChatData);
          if (hasChanged) {
            console.log(`✅ [REALTIME] Chat data updated from ${source}, new messages detected`);
            setTimeout(scrollToBottom, 100);
          }
          return newChatData;
        });

        // When a lead message is displayed, emit so Suggested Response refetches without reload (works for SSE, polling, or any source)
        if (hasNewClientMessage) {
          console.log('📨 [REALTIME] New lead message detected, emitting event for suggested response refresh');
          setTimeout(() => {
            console.log('📡 [REALTIME] Emitting client message event for suggested response generation');
            emitClientMessageEvent();
          }, 100);
        }
        
        setNextAction(nba || "");

        // Always sync Autopilot config so Scheduled persists after refresh
        try {
          const cfg = await apiService.getAutopilotConfig(leadId);
          if (cfg) {
            const nextType = cfg.answerType || 'Manual';
            const cur = latestLeadRef.current || {};
            if (nextType !== cur.answerType) {
              handleChangeLeadData(leadId, 'answerType', nextType);
            }
            if (nextType === 'Autopilot') {
              if ((cfg.instantly ?? true) !== (cur.instantly ?? true)) {
                handleChangeLeadData(leadId, 'instantly', cfg.instantly ?? true);
              }
              if (cfg.autopilotTime !== cur.autopilotTime) {
                handleChangeLeadData(leadId, 'autopilotTime', cfg.autopilotTime);
              }
              if (cfg.parsedTime && cfg.parsedTime !== (cur.parsedTime as any)) {
                handleChangeLeadData(leadId, 'parsedTime', cfg.parsedTime);
              }
            }
          }
        } catch (e) {
          console.warn('[LeadContent] Failed to load autopilot config', e);
        }

        // Fetch AI-extracted lead insights and fill only empty fields
        try {
          const insights = await apiService.getLeadInsights(leadId);
          console.log('[LeadContent] Fetched insights:', insights);
          setInsightsState(insights || {});
          
          const mappings: Array<{ key: string; value: any }> = [
            { key: 'motive', value: insights.motive },
            { key: 'buyOrSell', value: insights.buyOrSell },
            { key: 'timeframe', value: insights.timeframe },
            { key: 'agentStatus', value: insights.agentStatus },
            { key: 'firstTimeBuyer', value: insights.firstTimeBuyer },
            { key: 'income', value: insights.income },
            { key: 'homeValue', value: insights.homeValue },
            { key: 'creditScore', value: insights.creditScore },
            { key: 'cashOrMortgage', value: insights.cashOrMortgage },
            { key: 'employment', value: insights.employment },
            { key: 'otherHomes', value: insights.otherHomes },
          ];
          
          const cur = latestLeadRef.current || {};
          for (const { key, value } of mappings) {
            const isEmpty = cur[key] === undefined || cur[key] === '' || cur[key] === null;
            const hasValue = typeof value === 'boolean' ? true : Boolean(value && String(value).trim());
            
            // Update local state if field is empty and we have a value from insights
            if (hasValue && isEmpty) {
              handleChangeLeadData(leadId, key, value);
            }
            // Also update insightsState to always show the latest hybrid data
            else if (hasValue) {
              // Even if the lead already has data, update insights state to show the latest
              // This ensures the grid shows the most recent data from the backend
              console.log(`[LeadContent] Updating insightsState for ${key}:`, value);
            }
          }
        } catch (err) {
          console.warn('[LeadContent] Failed to load lead insights', err);
        }
      } catch (e) {
        console.warn("Failed to load lead data", e);
        if (cancelled) return;
        setChatData([]);
        setNextAction("");
      } finally {
        // no-op
      }
    };

    const setupSSE = () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          console.warn('🔕 [SSE] No access token, skipping SSE setup');
          return;
        }

        const baseURL = process.env.NODE_ENV === 'production' 
          ? 'https://api.myhomey.ai' 
          : 'http://localhost:5001';
        
        // Keep compatibility with backend SSE that accepts token via query param
        const url = `${baseURL}/v1/chats/updates?leadId=${leadId}&authorization=${encodeURIComponent(`Bearer ${token}`)}`;
        
        console.log(`🔔 [SSE] Setting up connection for lead: ${leadId}`);
        
        eventSource = new EventSource(url);
        
        eventSource.onopen = () => {
          console.log('✅ [SSE] Connection established');
        };
        
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 [SSE] Received event:', data.type);
            
            if (data.type === 'new_message') {
              console.log('🚀 [SSE] New message detected, fetching latest data');
              fetchChatData(false, 'SSE');
            }
          } catch (e) {
            console.warn('⚠️ [SSE] Failed to parse event data:', e);
          }
        };
        
        eventSource.onerror = (error) => {
          console.error('❌ [SSE] Connection error:', error);
          if (eventSource && eventSource.readyState === EventSource.CLOSED) {
            console.log('🔄 [SSE] Connection closed, will rely on fallback polling');
          }
        };
      } catch (error) {
        console.error('❌ [SSE] Setup failed:', error);
      }
    };

    // Initial fetch
    fetchChatData(false, 'initial');

    // Setup SSE connection for real-time updates
    setupSSE();

    // Fallback polling (reduced frequency since we have SSE)
    pollInterval = setInterval(() => {
      if (!cancelled) {
        fetchChatData(false, 'fallback-poll');
      }
    }, 30000); // Poll every 30 seconds as fallback

    return () => {
      cancelled = true;
      
      if (eventSource) {
        console.log('🔌 [SSE] Closing connection');
        eventSource.close();
      }
      
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, handleChangeLeadData]);

  useEffect(() => {
    setTimeout(scrollToBottom, 0);
  }, [chatData]);

  const emitClientMessageEvent = () => {
    console.log('📡 [DEBUG] Emitting client message event for leadId:', String(activeId));
    window.dispatchEvent(
      new CustomEvent("myhomey:new-client-message", {
        detail: { leadId: String(activeId), messageId: String(Date.now()) },
      })
    );
  };

  // Removed manual refresh handler
  /* const handleManualRefresh = () => {
    console.log('🔄 [MANUAL REFRESH] Refreshing chat data manually');
    const leadId = String(activeId ?? "");
    if (!leadId) return;
    
    // Call the fetch function with manual refresh flag
    const fetchChatData = async () => {
      try {
        setIsRefreshing(true);
        const [conversations, nba] = await Promise.all([
          apiService.getLeadConversations(leadId),
          apiService.getNextBestAction(leadId).catch(() => ""),
        ]);
        
        const mapConversationsToChatItems = (items: any[]): ChatItem[] => {
          return (items || []).map((it, idx) => {
            const id = Number(it?.id ?? idx + 1);
            const createdAt = it?.createdAt ?? it?.timestamp ?? undefined;
            const content = it?.content ?? it?.message ?? '';
            const rawRole = String(it?.role ?? it?.author ?? it?.authorType ?? '').toLowerCase();
            const rawDir = String(it?.direction ?? it?.dir ?? '').toLowerCase();

            const isMyMessage =
              rawDir === 'outbound' || rawDir === 'out'
                ? true
                : rawDir === 'inbound' || rawDir === 'in'
                  ? false
                  : ['agent', 'realtor', 'assistant', 'system'].includes(rawRole)
                    ? true
                    : ['client', 'lead', 'buyer', 'prospect', 'user'].includes(rawRole)
                      ? false
                      : false;

            return {
              id,
              isConversation: false,
              isMyMessage,
              minAgo: toRelativeTime(createdAt),
              message: String(content),
              direction: rawDir || undefined,
            } as ChatItem;
          });
        };
        
        const newChatData = mapConversationsToChatItems(conversations);
        setChatData(newChatData);
        setNextAction(nba || "");
        setTimeout(scrollToBottom, 100);
        console.log('✅ [MANUAL REFRESH] Chat data refreshed successfully');
      } catch (e) {
        console.warn("Failed to refresh lead data", e);
        setChatData([]);
        setNextAction("");
      } finally {
        setIsRefreshing(false);
      }
    };
    
    fetchChatData();
  }; */

  const handleChangeChatData = async (message: string, channel?: string): Promise<void> => {
    // CRITICAL DEBUG: Log the exact parameters being passed
    console.log('🔍 [DEBUG] handleChangeChatData called with:', {
      message: message.substring(0, 50) + '...',
      channel,
      activeId,
      timestamp: new Date().toISOString()
    });

    // CRITICAL FIX: Completely separate logic for external channels vs CHAT
    if (channel && channel !== 'CHAT') {
      console.log('🚀 [DEBUG] EXTERNAL CHANNEL - calling ONLY sendMessageWithChannel');
      console.log('🚀 [DEBUG] Channel:', channel, 'Message:', message.substring(0, 50) + '...');
      
      // optimistic: my message first
      const newId = chatData.length + 1;
      const newMessage: ChatItem = {
        id: newId,
        isConversation: false,
        isMyMessage: true,
        minAgo: toRelativeTime(Date.now()),
        message,
        direction: "outbound",
        channel: channel,
        deliveryStatus: "PENDING",
      };
      setChatData((prev) => [...prev, newMessage]);
      setTimeout(scrollToBottom, 0);

      try {
        console.log('🚀 [DEBUG] About to call sendMessageWithChannel...');
        const result = await apiService.sendMessageWithChannel(String(activeId), message, channel);
        console.log('✅ [DEBUG] sendMessageWithChannel completed:', result);
        
        // Update the message with delivery status
        if (result.messageId) {
          setChatData((prev) => prev.map(msg => 
            msg.id === newId 
              ? { ...msg, deliveryStatus: result.deliveryStatus, messageId: result.messageId }
              : msg
          ));
        }
        
        console.log('✅ [DEBUG] External channel message sent - NO AI response created');
        console.log('✅ [DEBUG] EXITING - will NOT call getClientReply');
        return; // CRITICAL: Exit here, do NOT call getClientReply
      } catch (err) {
        console.error("🚨 [DEBUG] Error in external channel send:", err);
        return;
      }
    }

    // CHAT CHANNEL ONLY - get AI response
    console.log('🤖 [DEBUG] CHAT CHANNEL - calling getClientReply');
    console.log('🤖 [DEBUG] Channel:', channel, 'Message:', message.substring(0, 50) + '...');
    
    // optimistic: my message first
    const newId = chatData.length + 1;
    const newMessage: ChatItem = {
      id: newId,
      isConversation: false,
      isMyMessage: true,
      minAgo: toRelativeTime(Date.now()),
      message,
      direction: "outbound",
      channel: channel,
      deliveryStatus: "PENDING",
    };
    setChatData((prev) => [...prev, newMessage]);
    setTimeout(scrollToBottom, 0);

    try {
      console.log('🤖 [DEBUG] About to call getClientReply for CHAT...');
      const result = await apiService.getClientReply(String(activeId), message);
      const response = result.response;
      console.log('🤖 [DEBUG] getClientReply completed, response:', response.substring(0, 50) + '...');

      const aiMessage: ChatItem = {
        id: newId + 1,
        isConversation: false,
        isMyMessage: false,
        minAgo: toRelativeTime(Date.now()),
        message: response,
      };

      // skip if same as previous
      setChatData((prev) => {
        const prevLast = prev[prev.length - 1] as any;
        if (prevLast && prevLast.message === aiMessage.message) return prev;
        return [...prev, aiMessage];
      });

      // notify SuggestedInput about new message
      emitClientMessageEvent();
      setTimeout(scrollToBottom, 0);
    } catch (err) {
      console.error("🚨 [DEBUG] Error in handleChangeChatData:", err);
      const aiError: ChatItem = {
        id: newId + 1,
        isConversation: false,
        isMyMessage: false,
        minAgo: toRelativeTime(Date.now()),
        message: "⚠️ AI service unavailable. Please try again.",
      };
      setChatData((prev) => [...prev, aiError]);
      emitClientMessageEvent();
      setTimeout(scrollToBottom, 0);
      console.error("AI error", err);
    }
  };

  const addMessagesWithDelay = async (
    messages: any[],
    delayMs: number = 1000
  ) => {
    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];

      // Treat non-owner single bubbles as client replies (AI roleplay buyer)
      if (message && message.isMyMessage === false && message.isConversation !== true) {
        const clientText = (message as any)?.message || "";
        setChatData((prev) => [...prev, { ...message, message: clientText }]);
      } else {
        setChatData((prev) => [...prev, message]);
      }

      if (i < messages.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef?.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  };

  const startEdit = (fieldKey: string, initialValue: any) => {
    setEditingField(fieldKey);
    setEditValues((prev) => ({ ...prev, [fieldKey]: initialValue ?? "" }));
  };

  const commitEdit = async (fieldKey: string) => {
    if (!lead?.id) return;
    const value = editValues[fieldKey];
    try {
      console.log('[LeadContent.commitEdit] field', fieldKey, 'value', value, 'leadId', lead.id);
      await (apiService as any).updateLeadField(lead.id, fieldKey, value);
      
      // Refresh local state via provided handler
      if (handleChangeLeadData) {
        await handleChangeLeadData(lead.id, fieldKey, value);
      }
      
      // Refresh insights to get the latest data (hybrid AI + manual)
      try {
        const insights = await apiService.getLeadInsights(lead.id);
        console.log('[LeadContent.commitEdit] Refreshed insights:', insights);
        setInsightsState(insights || {});
        
        // Update lead data with the refreshed insights
        if (handleChangeLeadData && insights) {
          const insightFields = ['motive', 'buyOrSell', 'timeframe', 'agentStatus', 'firstTimeBuyer', 
                                 'income', 'homeValue', 'creditScore', 'cashOrMortgage', 'employment', 'otherHomes'];
          for (const field of insightFields) {
            if (insights[field] !== undefined && insights[field] !== null && insights[field] !== '') {
              handleChangeLeadData(lead.id, field, insights[field]);
            }
          }
        }
      } catch (err) {
        console.warn('[LeadContent.commitEdit] Failed to refresh insights after update', err);
      }
    } catch (e) {
      console.error('Failed to update field', fieldKey, e);
    } finally {
      setEditingField(null);
    }
  };

  const renderEditable = (
    label: string,
    fieldKey: string,
    value: any,
    type: 'text' | 'number' | 'checkbox' = 'text'
  ) => {
    const isEditing = editingField === fieldKey;
    if (!isEditing) {
      return (
        <p className="text-h3 font-medium text-pureBlack whitespace-pre-wrap break-words leading-7">
          {type === 'checkbox' ? (value ? 'Yes' : 'No') : (value ?? '')}
        </p>
      );
    }
    return (
      <div className="flex items-center gap-2">
        {type === 'checkbox' ? (
          <label className="flex items-center gap-2 text-h3 text-pureBlack">
            <input
              type="checkbox"
              checked={Boolean(editValues[fieldKey])}
              onChange={(e) => setEditValues((p) => ({ ...p, [fieldKey]: e.target.checked }))}
            />
            <span>{label}</span>
          </label>
        ) : (
          <input
            type={type}
            className="h-9 px-3 py-2 rounded-xl bg-lightestGray border border-lighterGray w-full"
            value={type === 'number' ? (editValues[fieldKey] ?? '') : (editValues[fieldKey] ?? '')}
            onChange={(e) => setEditValues((p) => ({ ...p, [fieldKey]: type === 'number' ? Number(e.target.value) : e.target.value }))}
            placeholder={label}
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(fieldKey); if (e.key === 'Escape') setEditingField(null); }}
          />
        )}
      </div>
    );
  };

  const lead = leads.find((lead) => String(lead.id) === String(activeId));

  const customerPropertyItems = (() => {
    if (!lead) return [] as any[];
    const location = [lead?.address, lead?.city, lead?.state, lead?.zipCode]
      .filter(Boolean)
      .join(", ");
    const area = lead?.squareFootage ? `${lead.squareFootage} sqft` : null;
    const rooms = typeof lead?.bedrooms !== "undefined" ? lead?.bedrooms : null;
    const bathrooms = typeof lead?.bathrooms !== "undefined" ? lead?.bathrooms : null;
    const hasAny = Boolean(location || area || rooms !== null || bathrooms !== null);
    return hasAny ? [{ id: 1, location, area, rooms, bathrooms }] : [];
  })();


  const persistInterestedProperties = async (items: any[]) => {
    if (!lead?.id) return;
    try {
      // Send interestedProperties directly to the backend
      await (apiService as any).updateLeadField(lead.id, 'interestedProperties', items);

      // Also update local state immediately for UI consistency
      if (handleChangeLeadData) {
        const currentRaw = (lead as any)?.rawLead || {};
        const updatedPayload = { ...currentRaw, interestedProperties: items };
        await handleChangeLeadData(lead.id, 'rawLead', updatedPayload);
        await handleChangeLeadData(lead.id, 'rawJson', updatedPayload);
      }

      // Fetch the latest interested properties from the API
      try {
        const result = await apiService.getInterestedProperties(lead.id);
        setInterestedProperties(result.interestedProperties || []);
      } catch (fetchError) {
        console.warn('Failed to fetch updated interested properties, using local data:', fetchError);
        setInterestedProperties(items);
      }
    } catch (e) {
      console.error('Failed to persist interestedProperties', e);
    }
  };

  const addInterestedProperty = async () => {
    const loc = String(newProperty.location || '').trim();
    if (!loc) { setAddingProperty(false); return; }
    const item = {
      id: Date.now(),
      location: loc,
      area: newProperty.area || undefined,
      rooms: newProperty.rooms !== '' ? Number(newProperty.rooms) : undefined,
      bathrooms: newProperty.bathrooms !== '' ? Number(newProperty.bathrooms) : undefined,
    };
    const updated = [...interestedProperties, item];
    await persistInterestedProperties(updated);
    setNewProperty({ location: '', area: '', rooms: '', bathrooms: '' });
    setAddingProperty(false);
  };

  const removeInterestedProperty = async (id: any) => {
    const updated = interestedProperties.filter((p: any) => p.id !== id);
    await persistInterestedProperties(updated);
  };

  const informationSections = (
    [
      { id: 1, title: "Motive", key: 'motive', value: (insightsState as any)?.motive ?? (lead as any)?.motive ?? '', type: 'text' as const },
      { id: 2, title: "Buy and /or sell", key: 'buyOrSell', value: (insightsState as any)?.buyOrSell ?? (lead as any)?.buyOrSell ?? '', type: 'text' as const },
      { id: 3, title: "Timeframe", key: 'timeframe', value: (insightsState as any)?.timeframe ?? (lead as any)?.timeframe ?? '', type: 'text' as const },
      { id: 4, title: "Agent Status", key: 'agentStatus', value: (insightsState as any)?.agentStatus ?? (lead as any)?.agentStatus ?? '', type: 'text' as const },
      { id: 5, title: "First-Time HomeBuyer", key: 'firstTimeBuyer', value: Boolean((insightsState as any)?.firstTimeBuyer ?? (lead as any)?.firstTimeBuyer) as any, type: 'checkbox' as const },
      { id: 6, title: "Income", key: 'income', value: (insightsState as any)?.income ?? (lead as any)?.income ?? '', type: 'text' as const },
      { id: 7, title: "Budget/Home Value", key: 'homeValue', value: (insightsState as any)?.homeValue ?? (lead as any)?.homeValue ?? '', type: 'text' as const },
      { id: 8, title: "Credit Score", key: 'creditScore', value: (insightsState as any)?.creditScore ?? (lead as any)?.creditScore ?? '', type: 'text' as const },
      { id: 9, title: "Cash or Mortgage", key: 'cashOrMortgage', value: (insightsState as any)?.cashOrMortgage ?? (lead as any)?.cashOrMortgage ?? '', type: 'text' as const },
      { id: 10, title: "Employment", key: 'employment', value: (insightsState as any)?.employment ?? (lead as any)?.employment ?? '', type: 'text' as const },
      { id: 11, title: "Other Homes", key: 'otherHomes', value: (insightsState as any)?.otherHomes ?? (lead as any)?.otherHomes ?? '', type: 'text' as const },
    ] as { id: number; title: string; key: string; value: any; type: 'text'|'number'|'checkbox' }[]
  );

  return (
    <div className="flex flex-col flex-1 rounded-2xl border border-lighterGray bg-lightestGray mh-box-shadow h-full">
      {/* Hide scrollbars utility for overview card content */}
      <style>{`
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
      <div className="flex gap-x-3 px-6 py-5 h-[72px] border-b border-lighterGray sticky top-0 bg-lightestGray items-center rounded-t-2xl z-10">
        <div className="flex items-center gap-x-3 pr-4 border-r border-lighterGray">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center justify-center text-darkGray hover:text-darkBlue h-[40px] w-[40px]"
            >
              <i className="fa-solid fa-arrow-left text-xl"></i>
            </button>
          )}
          <p className="text-h2 font-semibold text-pureBlack">{lead?.name}</p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRemainderOpen(true);
            }}
            ref={bellRef}
          >
            {lead?.notification ? (
              <i className="fa-solid fa-bell text-darkerNavy"></i>
            ) : (
              <i className="fa-regular fa-bell text-gray hover:text-darkBlue"></i>
            )}
          </button>
        </div>
        <div className="flex justify-between items-center flex-1">
          <Button
            id="profile-button"
            onClick={() => setProfileIsOpen(!profileIsOpen)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              color: "darkestSlate",
              border: "1px solid",
              borderColor: "lightGray",
              fontFamily: "Inter , sans-serif",
              textTransform: "none",
              paddingLeft: "20px",
              paddingRight: "20px",
              borderRadius: "24px",
              height: "40px",
              "&:hover": {
                backgroundColor: "transparent",
                boxShadow: "none",
              },
            }}
          >
            <i className="fa-solid fa-user text-darkestSlate"></i>
            <p className="text-h3 font-medium text-deepBlack hidden lg:block">
              {!profileIsOpen ? "Profile Information" : "Chat"}
            </p>
            <i
              className={`fa-solid text-deepBlack fa-chevron-${profileIsOpen ? "up" : "down"
                }`}
            ></i>
          </Button>
          {!isMobileView && (
            <ResponseButtonWithMenu
              lead={lead}
              handleChangeLeadData={handleChangeLeadData}
            />
          )}
        </div>
        <EllipsisButtonWithMenu
          isMobileView={isMobileView}
          handleDeleteLead={handleDeleteLead}
          lead={lead}
          handleChangeLeadData={handleChangeLeadData}
          handleClearChat={handleClearChat}
        />
      </div>
      {!profileIsOpen ? (
        <div className="flex flex-col flex-1 h-[calc(100vh-180px)] overflow-hidden">
          <ChatHeader leadId={lead?.id} />
          <div className="flex-1 p-4 overflow-y-auto" ref={messagesEndRef}>
            <ChatContent chatData={chatData} />
          </div>
          <ChatFooter
            leadId={lead?.id}
            isSuggested={lead?.answerType === "Suggested"}
            handleChangeChatData={handleChangeChatData}
            addMessagesWithDelay={addMessagesWithDelay}
            answerType={lead?.answerType}
          />
        </div>
      ) : (
        <div className="flex-1 p-4 overflow-y-auto">
          <div className="flex flex-col gap-y-3">
            {/* Next Best Action */}
            {/* <NextBestAction 
              leadId={String(activeId)}
              nextAction={nextAction}
              onActionTaken={(action) => {
                console.log('Action taken:', action);
              }}
            /> */}
            
            {/* Scheduling Interface moved to ellipsis menu; hide default trigger button */}
            <SchedulingInterface 
              leadId={String(activeId)} 
              onSchedule={(result) => {
                console.log('Appointment scheduled:', result);
              }}
              showTriggerButton={false}
            />
            <div className="px-5 py-4 rounded-2xl border border-lightGray">
              <div className="flex flex-col md:flex-row lg:flex-col xl:flex-row justify-between items-center gap-2 pb-3 text-darkGray">
                <p className="text-h3 font-semibold">
                  Customer's Property and Contact Information:
                </p>
                <div className="flex gap-x-6 text-h4">
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-phone pr-2"></i>
                    {editingField === 'phone' ? (
                      <>
                        <input
                          className="h-8 px-2 py-1 rounded bg-lightestGray border border-lighterGray"
                          value={editValues['phone'] ?? lead?.phone ?? ''}
                          onChange={(e) => setEditValues((p) => ({ ...p, phone: e.target.value }))}
                          placeholder="Phone"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') commitEdit('phone'); }}
                        />
                        <button className="text-h4 text-darkBlue underline font-semibold" onClick={() => commitEdit('phone')}>Save</button>
                        <button className="text-h4 text-mediumGray underline" onClick={() => setEditingField(null)}>Cancel</button>
                      </>
                    ) : (
                      <button 
                        onClick={async (e) => {
                          e.preventDefault();
                          if (!lead?.phone) { startEdit('phone', ''); return; }
                          try {
                            const result = await apiService.initiateWebToPhoneCall(lead.id, `Calling ${lead.name || 'Lead'}`);
                            if (result.success) {
                              setActiveCall({ callId: result.callId!, conferenceName: result.conferenceName, status: result.status });
                            } else {
                              alert(`Failed to initiate call: ${result.error}`);
                            }
                          } catch (error) {
                            alert(`Error initiating call: ${error}`);
                          }
                        }}
                        className="font-bold hover:text-darkBlue transition-colors duration-200 cursor-pointer bg-transparent border-none p-0 text-left"
                        title={lead?.phone ? `Call ${formatPhoneNumber(lead.phone)} via Web-to-Phone` : 'Set phone number'}
                      >
                        {lead?.phone ? formatPhoneNumber(lead.phone) : 'Set phone number'}
                      </button>
                    )}
                    {editingField !== 'phone' && (
                      <button title="Edit phone" aria-label="Edit phone" className="text-h4 text-darkBlue" onClick={() => startEdit('phone', lead?.phone)}>
                        <i className="fa-solid fa-pen"></i>
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="fa-solid fa-envelope pr-2"></i>
                    {editingField === 'email' ? (
                      <>
                        <input
                          className="h-8 px-2 py-1 rounded bg-lightestGray border border-lighterGray"
                          value={editValues['email'] ?? lead?.email ?? ''}
                          onChange={(e) => setEditValues((p) => ({ ...p, email: e.target.value }))}
                          placeholder="Email"
                          autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter') commitEdit('email'); }}
                        />
                        <button className="text-h4 text-darkBlue underline font-semibold" onClick={() => commitEdit('email')}>Save</button>
                        <button className="text-h4 text-mediumGray underline" onClick={() => setEditingField(null)}>Cancel</button>
                      </>
                    ) : (
                      <a 
                        href={lead?.email ? `mailto:${lead.email}` : undefined}
                        className="font-bold hover:text-darkBlue transition-colors duration-200 cursor-pointer"
                        title={lead?.email ? `Email ${lead.email}` : 'Set email address'}
                        onClick={(e) => { if (!lead?.email) { e.preventDefault(); startEdit('email', ''); } }}
                      >
                        {lead?.email || 'Set email address'}
                      </a>
                    )}
                    {editingField !== 'email' && (
                      <button title="Edit email" aria-label="Edit email" className="text-h4 text-darkBlue" onClick={() => startEdit('email', lead?.email)}>
                        <i className="fa-solid fa-pen"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <ul className="flex flex-col gap-y-3">
                {customerPropertyItems.length === 0 ? (
                  <li className="px-4 py-3 rounded-lg border border-lightGray gap-6">
                    <div className="mh-text-grid gap-6">
                      <p className="text-h3 font-medium text-pureBlack truncate">
                        <i className="fa-solid fa-location-dot pr-2.5 text-darkGray"></i>
                        Client Information Needed
                      </p>
                    </div>
                  </li>
                ) : (
                  customerPropertyItems.map((property) => (
                    <li className="px-4 py-3 rounded-lg border border-lightGray gap-6" key={property.id}>
                      <div className="mh-text-grid gap-6">
                        <p className="text-h3 font-medium text-pureBlack truncate">
                          <i className="fa-solid fa-location-dot pr-2.5 text-darkGray"></i>
                          {property.location || "Client Information Needed"}
                        </p>
                        <ul className="flex justify-start md:justify-end lg:justify-start xl:justify-end gap-x-2.5">
                          {property.area && (
                            <li className="flex items-center h-6 px-3 bg-lightestBlue rounded-[32px] text-h4 font-bold text-darkGray whitespace-nowrap">
                              <i className="fa-solid fa-border-none pr-2"></i>
                              {property.area}
                            </li>
                          )}
                          {typeof property.rooms !== "undefined" && property.rooms !== null && (
                            <li className="flex items-center h-6 px-3 bg-lightestBlue rounded-[32px] text-h4 font-bold text-darkGray">
                              <i className="fa-solid fa-bed pr-2"></i>
                              {property.rooms}
                            </li>
                          )}
                          {typeof property.bathrooms !== "undefined" && property.bathrooms !== null && (
                            <li className="flex items-center h-6 px-3 bg-lightestBlue rounded-[32px] text-h4 font-bold text-darkGray">
                              <i className="fa-solid fa-bath pr-2"></i>
                              {property.bathrooms}
                            </li>
                          )}
                        </ul>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
            {/* Overview cards will use the existing card section below; extra grid removed */}
            <div className="px-5 py-4 rounded-2xl border border-lightGray">
              <div className="flex flex-col md:flex-row justify-between items-center gap-2 pb-3 text-darkGray">
                <p className="text-h3 font-semibold">Interested Properties:</p>
                {!addingProperty ? (
                  <button className="text-h4 font-bold" onClick={() => setAddingProperty(true)}>
                    <i className="fa-solid fa-circle-plus pr-2"></i>
                    Add Property
                  </button>
                ) : (
                  <div className="w-full md:w-auto flex flex-col md:flex-row gap-2 mt-2 md:mt-0">
                    <input className="h-9 px-3 py-2 rounded bg-lightestGray border border-lighterGray" placeholder="Location" value={newProperty.location} onChange={(e)=>setNewProperty((p:any)=>({...p,location:e.target.value}))} />
                    <input className="h-9 px-3 py-2 rounded bg-lightestGray border border-lighterGray" placeholder="Area (e.g., 1800 sqft)" value={newProperty.area} onChange={(e)=>setNewProperty((p:any)=>({...p,area:e.target.value}))} />
                    <input className="h-9 px-3 py-2 rounded bg-lightestGray border border-lighterGray w-20" placeholder="Beds" type="number" value={newProperty.rooms} onChange={(e)=>setNewProperty((p:any)=>({...p,rooms:e.target.value}))} />
                    <input className="h-9 px-3 py-2 rounded bg-lightestGray border border-lighterGray w-28" placeholder="Baths" type="number" value={newProperty.bathrooms} onChange={(e)=>setNewProperty((p:any)=>({...p,bathrooms:e.target.value}))} />
                    <button className="text-h4 text-darkBlue underline font-semibold" onClick={addInterestedProperty}>Save</button>
                    <button className="text-h4 text-mediumGray underline" onClick={()=>{setAddingProperty(false); setNewProperty({ location:'', area:'', rooms:'', bathrooms:''});}}>Cancel</button>
                  </div>
                )}
              </div>
              <ul className="flex flex-col gap-y-3">
                {interestedProperties.length === 0 ? (
                  <li className="px-4 py-3 rounded-lg border border-lightGray gap-6">
                    <div className="mh-text-grid gap-6">
                      <p className="text-h3 font-medium text-pureBlack truncate">
                        <i className="fa-solid fa-location-dot pr-2.5 text-darkGray"></i>
                        No interested properties yet
                      </p>
                    </div>
                  </li>
                ) : (
                  interestedProperties.map((property: any) => (
                    <li className="px-4 py-3 rounded-lg border border-lightGray gap-6" key={property.id}>
                      <div className="mh-text-grid gap-6">
                        <p className="text-h3 font-medium text-pureBlack truncate">
                          <i className="fa-solid fa-location-dot pr-2.5 text-darkGray"></i>
                          {property.location}
                        </p>
                        <ul className="flex justify-start md:justify-end lg:justify-start xl:justify-end gap-x-2.5">
                          {property.area && (
                            <li className="flex items-center h-6 px-3 bg-lightestBlue rounded-[32px] text-h4 font-bold text-darkGray whitespace-nowrap">
                              <i className="fa-solid fa-border-none pr-2"></i>
                              {property.area}
                            </li>
                          )}
                          {typeof property.rooms !== "undefined" && (
                            <li className="flex items-center h-6 px-3 bg-lightestBlue rounded-[32px] text-h4 font-bold text-darkGray">
                              <i className="fa-solid fa-bed pr-2"></i>
                              {property.rooms}
                            </li>
                          )}
                          {typeof property.bathrooms !== "undefined" && (
                            <li className="flex items-center h-6 px-3 bg-lightestBlue rounded-[32px] text-h4 font-bold text-darkGray">
                              <i className="fa-solid fa-bath pr-2"></i>
                              {property.bathrooms}
                            </li>
                          )}
                          <li>
                            <button className="text-h4 text-darkBlue underline font-semibold" onClick={()=>removeInterestedProperty(property.id)}>Remove</button>
                          </li>
                        </ul>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
            
            {/* AI-Suggested Properties from MLS */}
            {/* <div className="rounded-2xl border border-lightGray">
              <PropertyList leadId={activeId} limit={6} />
            </div> */}
            
            <div className="px-5 py-4 rounded-2xl border border-lightGray bg-lightestBlue">
              <p className="text-h3 font-semibold text-darkGray inline pr-2">
                Next Best Action:
              </p>
              <p className="text-h3 font-mudium text-pureBlack inline">
                {(() => {
                  const map: Record<string, string> = {
                    ASK_MOTIVE: "Ask for motive",
                    ASK_TIMEFRAME: "Ask for timeframe",
                    ASK_BUDGET: "Ask for budget",
                    ASK_BUYER_TYPE: "Ask for buyer type",
                    ASK_LOCATION: "Ask for location",
                    ASK_PRICE_RANGE: "Ask for price range",
                    SCHEDULE_CALL: "Schedule a call",
                    SEND_LISTINGS: "Send listings",
                    FOLLOW_UP: "Follow up",
                  };
                  const label = map[nextAction];
                  if (label) return label;
                  if (!nextAction) return "Not available";
                  return nextAction.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
                })()}
              </p>
            </div>
            <div className="p-4 rounded-2xl border border-lightGray grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {(informationSections.length ? informationSections : [
                { id: 0, title: "Profile", description: "Client Information Needed" },
              ]).map((section) => (
                <div className="mh-box-shadow rounded-2xl h-[240px] flex flex-col" key={section.id}>
              <div className="h-[56px] flex justify-between items-center px-4 border-b border-lighterGray shrink-0">
                <p className="text-h3 font-medium text-pureBlack">{section.title}</p>
                {editingField === section.key ? (
                  <div className="flex items-center gap-4">
                    <button className="text-h4 text-darkBlue underline font-semibold" onClick={() => commitEdit(section.key)}>Save</button>
                    <button className="text-h4 text-mediumGray underline" onClick={() => setEditingField(null)}>Cancel</button>
                  </div>
                ) : (
                  <button title={`Edit ${section.title}`} aria-label={`Edit ${section.title}`} className="text-h4 text-darkBlue" onClick={() => startEdit(section.key, section.value)}>
                    <i className="fa-solid fa-pen"></i>
                  </button>
                )}
              </div>
                  {!section.value && editingField !== section.key ? (
                    <div className="p-4 flex items-center flex-1 min-h-0">
                      <div className="flex flex-col items-center gap-y-6 text-darkGray flex-1">
                        <i className="fa-regular fa-comments text-5xl"></i>
                        <p className="text-h3 font-medium">Client Information Needed</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 flex-1 min-h-0 overflow-y-auto no-scrollbar">
                      <div className="bg-lightestBlue rounded-tl-[4px] rounded-tr-[12px] rounded-br-[12px] rounded-bl-[12px] p-5">
                        {renderEditable(section.title, section.key, section.value, section.type)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="px-5 py-4 rounded-2xl border border-lightGray">
              <p className="text-h3 font-semibold text-darkGray mb-3">Notes:</p>
              <input
                type="text"
                className="p-4 border border-deepBlack rounded-lg bg-lightestGray w-full placeholder:text-h3 placeholder:text-pureBlack"
                placeholder={lead?.notes || "Enter your notes…"}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onBlur={async () => {
                  const text = noteDraft.trim();
                  if (!lead?.id || !text) return;
                  try {
                    await apiService.createLeadNote(lead.id, text);
                    if (handleChangeLeadData) {
                      handleChangeLeadData(lead.id, "notes", text);
                    }
                    setNoteDraft("");
                  } catch (err) {
                    console.error("Failed to save note", err);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
      {remainderOpen && (
        <Remainder
          isOpen={remainderOpen}
          notification={lead?.notification}
          onClose={() => setRemainderOpen(false)}
          anchorRef={bellRef}
          handleChangeLeadData={handleChangeLeadData}
          leadId={lead?.id}
          parsedNotificTime={lead?.parsedNotificTime}
        />
      )}
      {activeCall ? (
        <CallPanel
          callId={activeCall.callId}
          conferenceName={activeCall.conferenceName}
          initialStatus={activeCall.status}
          displayName={lead?.name}
          onClose={() => setActiveCall(null)}
        />
      ) : null}
    </div>
  );
};

export default LeadContent;